<template>
  <div class="avatar-container" :style="style" aria-hidden="true">
    <slot>{{ userInitial }}</slot>
  </div>
</template>

<script>
const AVATAR_TONES = 5;

export default {
  name: 'Avatar',
  props: {
    username: {
      type: String,
      default: '',
    },
    size: {
      type: Number,
      default: 40,
    },
  },
  computed: {
    style() {
      return {
        fontSize: `${Math.floor(this.size / 2.5)}px`,
        // One custom property carries the whole colour decision. Because `--avatar-N` is
        // redefined under `.dark`, `var()` resolves to the right pair on its own — a `:style`
        // binding could never have done that with a literal colour.
        '--avatar-tone': `var(--avatar-${this.toneIndex})`,
      };
    },

    // Deterministic, so a person keeps the same tone everywhere they appear and across
    // reloads. A sum of code points rather than a real hash: the input is a display name, the
    // output space is five, and anything stronger would be arithmetic for its own sake.
    // Unicode-safe by construction, which matters when most of these names are Arabic.
    toneIndex() {
      const name = this.username || '';
      let sum = 0;
      for (let i = 0; i < name.length; i += 1) {
        sum = (sum + name.codePointAt(i)) % 9973;
      }
      return (sum % AVATAR_TONES) + 1;
    },

    userInitial() {
      const parts = this.username.split(/[ -]/);
      let initials = parts.reduce((acc, curr) => acc + curr.charAt(0), '');

      if (initials.length > 2 && initials.search(/[A-Z]/) !== -1) {
        initials = initials.replace(/[a-z]+/g, '');
      }
      initials = initials.substring(0, 2).toUpperCase();

      return initials;
    },
  },
};
</script>

<style scoped>
/*
 * This was two hardcoded Chatwoot-blue gradients — #c2e1ff -> #d6ebff on light and a flat
 * #135899 on dark — answering to neither the OKLCH tokens nor the SCSS palette. On the most
 * repeated element in the product: every conversation row, every message, every contact.
 *
 * Now the wash and the initials come from ONE tone token, at two strengths. A flat tint rather
 * than a gradient: a 32px circle has no room to show a gradient, so all the gradient ever did
 * was make the shape look slightly dirty at small sizes.
 */
.avatar-container {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 100%;
  font-weight: 500;
  cursor: default;
  background-color: oklch(var(--avatar-tone) / var(--avatar-wash));
  color: oklch(var(--avatar-tone));
  /* An inset hairline of the tone itself, so the circle keeps an edge where the wash is nearly
     the same value as the surface behind it. */
  box-shadow: inset 0 0 0 1px oklch(var(--avatar-tone) / 0.22);
}
</style>
